import { useEffect, useState, useRef } from "react";
import { useUser } from "@descope/react-sdk";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "@mui/icons-material";
import { RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import SubscriptionCard from "../../../components/payment/SubscriptionCard";
import SubscriptionFinalCard from "src/components/payment/SubscriptionFinalCard";
import SubscriptionAddOnCard from "src/components/payment/SubscriptionAddOnCard";
import {
  getPlans,
  getTenantUsage,
  getAddons,
  getTenantPlan
} from "../../../redux/features/plan-billing/plan-billing-slice";
import Grid from "@mui/material/Grid2";
import { IStripeProduct } from "../../../types/product";

interface Addons {
  mailbox?: { priceId: string; quantity: number };
  sms?: { priceId: string };
  scheduling?: { priceId: string };
}

interface LocationState {
  addOnPage?: boolean;
  selectedPlan?: any;
  adminSeats?: number;
  mailBoxSeats?: number;
  mailAddCheck?: boolean;
  textAddCheck?: boolean;
  scheduleAddCheck?: boolean;
  selectedAddons?: Addons;
}

const ViewPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useUser();
  const location = useLocation();
  const initialLoadDone = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const [planId, setPlanId] = useState(0);
  const [addOnPage, setAddOnPage] = useState(false);
  const [adminSeats, setAdminSeats] = useState(0);
  const [textAddCheck, setTextAddCheck] = useState(false);
  const [mailAddCheck, setMailAddCheck] = useState(false);
  const [scheduleAddCheck, setScheduleAddCheck] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null); //main plan product
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null); //main plan product
  const [mailboxSeats, setMailboxSeats] = useState<number>(1);
  const [smsPriceId, setSmsPriceId] = useState<string>("");
  
  const [showCouponField, setShowCouponField] = useState(false);

  const [isMailboxSelected, setIsMailboxSelected] = useState<boolean>(false);
  const [isSmsSelected, setIsSmsSelected] = useState<boolean>(false);
  const [isSchedSelected, setIsSchedSelected] = useState<boolean>(false);

  const orgData = useSelector(
    (state: RootState) => state.organizationSlice.orgData
  );
  const products = useSelector(
    (state: RootState) => state.planBillingSlice.products
  );
  const mailProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.mailProducts
  );
  const textProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.textProducts
  );
  const scheduleProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.scheduleProducts
  );
  const tenantUsage = useSelector(
    (state: RootState) => state.planBillingSlice.tenantUsage
  );
  const currentPlan = useSelector(
    (state: RootState) => state.planBillingSlice.currentPlan
  );

  const [selectedAddons, setSelectedAddons] = useState<Addons>({});
  const state = location.state as LocationState;
  // console.log("viewPlan state.selectedAddons: ", state?.selectedAddons);

  useEffect(() => {
    // Prevent concurrent loads
    if (initialLoadDone.current || isLoading) return;

    const loadAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          dispatch(getPlans()),
          dispatch(getAddons()),
          // dispatch(getTenantPlan()),
          dispatch(getTenantUsage()),
        ]);

        initialLoadDone.current = true;
      } catch (error) {
        console.error("[viewPlan] Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, []); // Remove addOnPage dependency since it's not needed for initial load

  // Initialize state after data is loaded
  useEffect(() => {
    if (!initialLoadDone.current) return;

    // Handle location state after data is loaded
    if (state?.addOnPage) {
      setAddOnPage(true);
    }

    //also pre-populate from location state
    if (state?.selectedPlan) {
      setSelectedPlan(state?.selectedPlan);
      setSelectedProduct(state?.selectedPlan?.planDetail);
      setAdminSeats(state?.adminSeats || state?.selectedPlan?.adminSeats || 0);
    }
    setMailboxSeats(state?.mailBoxSeats || 1);
    setMailAddCheck(state?.mailAddCheck || false);
    setTextAddCheck(state?.textAddCheck || false);
    setScheduleAddCheck(state?.scheduleAddCheck || false);
    setSelectedAddons(state?.selectedAddons || {});

    // Set planId based on selected plan after data is loaded
    if (state?.selectedPlan && products.length > 0) {
      const planIndex = products.findIndex(
        (product) => product.productId === state.selectedPlan?.planDetail?.productId
      );
      if (planIndex !== -1) {
        setPlanId(planIndex);
      }
    }
  }, [initialLoadDone.current, state, products]);

  useEffect(() => {
    if (selectedPlan?.adminSeats !== undefined) {
      // console.log("Update adminSeats when selectedPlan changes");
      // console.log("set adminSeats", selectedPlan.adminSeats);
      setAdminSeats(selectedPlan?.adminSeats);
    }
  }, [selectedPlan?.adminSeats]);

  useEffect(() => {
    const isPlanUpdate = addOnPage && selectedProduct?.productId !== selectedPlan?.planDetail?.productId;
    const isAddOnOnly = addOnPage && selectedProduct?.productId === selectedPlan?.planDetail?.productId;
  
    // console.log("isPlanUpdate:", isPlanUpdate);
    // console.log("isAddOnOnly:", isAddOnOnly);

    if (isPlanUpdate || isAddOnOnly) {
      setShowCouponField(true);
    }

    // Update selectedPlan when selectedProduct changes
    if (isPlanUpdate) {
      setSelectedPlan({
        ...selectedPlan,
        planDetail: selectedProduct
      });
    }

    // populate user selected plan id (index) i.e. 0,1,2 
    if (selectedPlan) {
      // console.log("selectedPlan, products:", selectedPlan, products);
      
      const planIndex = products.findIndex(
        (product) => product.productId === selectedPlan?.planDetail?.productId
      );
      if (planIndex !== -1) {
        setPlanId(planIndex);
      }
    }

  }, [addOnPage, selectedProduct, selectedPlan, products]);
  // console.log("planId:", planId);

  useEffect(() => {
    if (!selectedAddons) return;

    // console.log("updated selectedAddons:", selectedAddons);
    if (selectedAddons.mailbox) {
      setMailboxSeats(selectedAddons.mailbox.quantity);      
      setIsMailboxSelected(true);
    }

    if (selectedAddons.sms) {
      setSmsPriceId(selectedAddons.sms.priceId);
      setIsSmsSelected(true);
    }

    if (selectedAddons.scheduling) {
      setIsSchedSelected(true);
    }
  }, [selectedAddons]);

  // console.log("currentPlan", currentPlan);
  // console.log("View-Plan selectedPlan:", selectedPlan);
  // console.log("adminSeats:", adminSeats, currentPlan?.adminSeats);
  // console.log("mailAddCheck:", mailAddCheck);
  // console.log("textAddCheck:", textAddCheck);
  // console.log("scheduleAddCheck:", scheduleAddCheck);
  // console.log("selectedAddons:", selectedAddons);
  // console.log("addOnPage:", addOnPage);
  // console.log("mailProducts:", planId, mailProducts[planId]);

  return (
    <>
      {(!initialLoadDone.current || isLoading) ? null : (
        <div className="pageTemplate">
          <div className="backHead">
            <Button
              className="backBtn"
              startIcon={<ArrowLeft />}
              style={{ marginBottom: 0 }}
              onClick={() => navigate("/my-plan")}
            >
              {!addOnPage ? (
                <Typography variant="h6" className="DetailsPageTitle">
                  All Plans
                </Typography>
              ) : (
                <Typography variant="h6" className="DetailsPageTitle">
                  Add-Ons
                </Typography>
              )}
            </Button>
          </div>

          {!addOnPage ? (
            <Grid container spacing={2} sx={{ p: "0px 100px" }}>
              {products.map((product) => (
                <SubscriptionCard
                  key={product.productId}
                  product={product}
                  currentPrice={selectedPlan?.planDetail?.price}
                  tenantUsage={tenantUsage}
                  adminSeats={adminSeats}
                  setAdminSeats={setAdminSeats}
                  setAddOnPage={setAddOnPage}
                  setPlanId={setPlanId}
                  setSelectedProduct={setSelectedProduct}
                />
              ))}
            </Grid>
          ) : (
            <>
              <Grid
                display="flex"
                justifyContent="space-between"
                sx={{ m: "0px 0px 10px 0px" }}
              >
                <Box>
                  <Typography variant="h5" fontWeight={550}>
                    Hi, {user?.name || "there"}! Would you like to add some other
                    features?
                  </Typography>
                  <Typography fontSize="18px" marginTop="10px">
                    You have selected a plan, here you can add additional features
                    to your plan.
                  </Typography>
                </Box>
              </Grid>

              <Grid container spacing={3}>
                <SubscriptionAddOnCard
                  product={mailProducts[planId]}
                  type={0}
                  setAddCheck={setMailAddCheck}
                  addCheck={mailAddCheck}
                />
                <SubscriptionAddOnCard
                  product={textProducts[planId]}
                  type={1}
                  setAddCheck={setTextAddCheck}
                  addCheck={textAddCheck}
                />
                <SubscriptionAddOnCard
                  product={scheduleProducts[planId]}
                  type={2}
                  setAddCheck={setScheduleAddCheck}
                  addCheck={scheduleAddCheck}
                />
              </Grid>

              <Grid container spacing={3}>
                <SubscriptionFinalCard
                  product={products[planId]}
                  mailProduct={mailProducts[planId]}
                  textProducts={textProducts}
                  smsPriceId={smsPriceId}
                  scheduleProduct={scheduleProducts[planId]}
                  mailAddCheck={mailAddCheck}
                  textAddCheck={textAddCheck}
                  scheduleAddCheck={scheduleAddCheck}
                  addOnPage={addOnPage}  // allow update addons
                  selectedPlan={selectedPlan}
                  adminSeats={adminSeats}
                  mailboxSeats={mailboxSeats}
                  showCouponField={true}
                  isUpdate={true} //update plan
                />
              </Grid>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ViewPlan;
